/**
 * Dashboard / Home page
 * Features: prompt input box, action buttons, inline PAIQ results
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@codespeak11/cognito-passwordless';
import { AxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Snackbar,
  Paper,
  Stack,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ImageIcon from '@mui/icons-material/Image';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CampaignIcon from '@mui/icons-material/Campaign';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { paiqApi, promptsApi, templatesApi, packsApi, semanticTreeApi, marketingPackApi, MarketingPackResult } from '../services/api';
import MarkdownView from '../components/MarkdownView';

type ActionType = 'ideas' | 'infographic' | 'semantic-tree' | 'prompt-pack' | 'marketing-pack';

interface PAIQDimensionScore { score: number; reasoning: string; }
interface PAIQDimensions {
  firstPrinciplesDepth?: PAIQDimensionScore;
  cognitiveConstraints?: PAIQDimensionScore;
  crossDomainSynthesis?: PAIQDimensionScore;
  specificity?: PAIQDimensionScore;
  signalToNoise?: PAIQDimensionScore;
  actionability?: PAIQDimensionScore;
  critiqueLoop?: PAIQDimensionScore;
}
interface PAIQResult {
  paiqScore: number; maxScore: number; paiqGrade: string;
  dimensions?: PAIQDimensions;
  strengths?: string[]; weaknesses?: string[]; improvements?: string[]; rewrittenPrompt?: string;
}
interface Prompt { entityId: string; promptName: string; description?: string; paiqScore?: number; createdAt: string; }
interface Template { entityId: string; templateName: string; description?: string; createdAt: string; }
interface Pack { entityId: string; promptPackName: string; description?: string; price?: number; createdAt: string; }

const ACTION_BUTTONS: { type: ActionType; label: string; icon: React.ReactNode; color: string }[] = [
  { type: 'ideas', label: 'Ideas', icon: <LightbulbIcon sx={{ fontSize: 36 }} />, color: '#f59e0b' },
  { type: 'infographic', label: 'Infographic', icon: <ImageIcon sx={{ fontSize: 36 }} />, color: '#6366f1' },
  { type: 'semantic-tree', label: 'Semantic Tree', icon: <AccountTreeIcon sx={{ fontSize: 36 }} />, color: '#10b981' },
  { type: 'prompt-pack', label: 'Prompt Pack', icon: <LibraryBooksIcon sx={{ fontSize: 36 }} />, color: '#3b82f6' },
  { type: 'marketing-pack', label: 'Marketing Pack', icon: <CampaignIcon sx={{ fontSize: 36 }} />, color: '#ec4899' },
];

function DimensionBar({ label, score, max = 5 }: { label: string; score: number; max?: number }) {
  const pct = (score / max) * 100;
  const color = pct >= 80 ? 'success' : pct >= 60 ? 'info' : pct >= 40 ? 'warning' : 'error';
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight="bold">{score}/{max}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} color={color as 'success' | 'info' | 'warning' | 'error'} sx={{ height: 8, borderRadius: 1 }} />
    </Box>
  );
}

function gradeColor(grade: string): 'success' | 'info' | 'warning' | 'error' | 'default' {
  const g = grade.toLowerCase();
  if (g === 'exceptional' || g === 'excellent') return 'success';
  if (g === 'good') return 'info';
  if (g === 'adequate') return 'warning';
  return 'error';
}

export default function Dashboard() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [promptText, setPromptText] = useState('');
  const MAX_CHARS = 1000;
  const MIN_CHARS = 20;
  const buttonsEnabled = promptText.trim().length >= MIN_CHARS;

  const [activeAction, setActiveAction] = useState<ActionType | null>(null);
  const [loading, setLoading] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [suggestedTitle, setSuggestedTitle] = useState('');
  const [paiqResult, setPaiqResult] = useState<PAIQResult | null>(null);
  const [showPaiqDetails, setShowPaiqDetails] = useState(false);
  const [showDimensions, setShowDimensions] = useState(true);
  const [marketingPackResult, setMarketingPackResult] = useState<MarketingPackResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [packs, setPacks] = useState<Pack[]>([]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const fetchData = async () => {
      setLibraryLoading(true);
      setLibraryError(null);
      try {
        const [pr, tr, pkr] = await Promise.all([promptsApi.list(), templatesApi.list(), packsApi.list()]);
        setPrompts(pr?.data || pr || []);
        setTemplates(tr?.data || tr || []);
        setPacks(pkr?.data || pkr || []);
      } catch { setLibraryError('Failed to load library data.'); }
      finally { setLibraryLoading(false); }
    };
    void fetchData();
  }, [authLoading, isAuthenticated]);

  useEffect(() => { setSearchQuery(searchParams.get('q') || ''); }, [searchParams]);

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return {
      prompts: prompts.filter(p => p.promptName.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)),
      templates: templates.filter(t => t.templateName.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)),
      packs: packs.filter(p => p.promptPackName.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)),
    };
  }, [searchQuery, prompts, templates, packs]);

  const recentPrompts = useMemo(() => [...prompts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5), [prompts]);
  const recentTemplates = useMemo(() => [...templates].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5), [templates]);
  const recentPacks = useMemo(() => [...packs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5), [packs]);
  const resultPrompt = activeAction === 'marketing-pack'
    ? marketingPackResult?.marketingPlan ?? ''
    : generatedPrompt;

  const extractApiErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof AxiosError) {
      const payload = error.response?.data as
        | { error?: { message?: string }; message?: string }
        | undefined;
      return payload?.error?.message || payload?.message || error.message || fallback;
    }
    if (error instanceof Error) return error.message || fallback;
    return fallback;
  };

  const handleAction = async (actionType: ActionType) => {
    if (!buttonsEnabled) return;
    setActiveAction(actionType);
    setLoading(true);
    setActionError(null);
    setGeneratedPrompt('');
    setSuggestedTitle('');
    setPaiqResult(null);
    setShowPaiqDetails(false);
    setShowDimensions(true);
    setMarketingPackResult(null);
    setSaved(false);
    try {
      if (actionType === 'ideas') {
        const smartRes = await paiqApi.generateSmartPrompt(promptText);
        const smart = smartRes?.data ?? smartRes;
        const generated = smart?.smartPrompt ?? promptText;
        setGeneratedPrompt(generated);
        if (typeof smart?.suggestedTitle === 'string' && smart.suggestedTitle.trim()) {
          setSuggestedTitle(smart.suggestedTitle.trim());
        }
        const iqRes = await paiqApi.calculateIQ(generated);
        const iq = iqRes?.data ?? iqRes;
        setPaiqResult(iq);
      } else if (actionType === 'semantic-tree') {
        const treeRes = await semanticTreeApi.generate(promptText);
        const treeData = treeRes?.data ?? treeRes;
        const improvedPrompt = treeData?.improvedPrompt ?? promptText;
        setGeneratedPrompt(improvedPrompt);
        setPaiqResult({
          paiqScore: treeData?.paiqScore || 0,
          maxScore: 35,
          paiqGrade: treeData?.paiqGrade || 'Adequate',
          strengths: [
            'Semantic extraction identifies key themes and hierarchy.',
            'Generated image prompt follows editorial standards.',
          ],
          weaknesses: [
            'Semantic tree complexity depends on source text clarity.',
          ],
          improvements: [
            'Refine source text for clearer conceptual structure.',
            'Consider providing chapter titles or section headings.',
          ],
          rewrittenPrompt: improvedPrompt,
        });
      } else if (actionType === 'marketing-pack') {
        const mpRes = await marketingPackApi.generate(promptText);
        const mpData = mpRes?.data ?? mpRes;
        setMarketingPackResult(mpData);
        // Keep the full plan as the single prompt surface for results and analysis.
        setGeneratedPrompt('');
        setPaiqResult({
          paiqScore: mpData?.paiqScore || 0,
          maxScore: 35,
          paiqGrade: mpData?.paiqGrade || 'Adequate',
          strengths: [
            `Generated marketing pack with ${mpData?.assetCount || 0} asset prompt(s).`,
            `Target audience identified: ${mpData?.targetAudience || 'see plan'}.`,
          ],
          weaknesses: [
            'Review assumptions in the plan for accuracy.',
          ],
          improvements: [
            'Add specific proof points and differentiators to your description.',
            'Include pricing, channels, and launch timeline for richer output.',
          ],
          rewrittenPrompt: mpData?.marketingPlan,
        });
      } else {
        throw new Error(`The "${actionType}" action is coming soon.`);
      }
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Action failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    const originalPrompt = promptText.trim();
    const optimizedPrompt = resultPrompt.trim();
    const content = originalPrompt || optimizedPrompt;
    if (!content) return;

    const safePaiqScore =
      typeof paiqResult?.paiqScore === 'number' && Number.isFinite(paiqResult.paiqScore)
        ? Math.max(0, Math.min(100, paiqResult.paiqScore))
        : undefined;

    setSaving(true);
    try {
      const promptName = suggestedTitle.trim() || `${activeAction ?? 'Prompt'} - ${new Date().toLocaleDateString()}`;
      const response = await promptsApi.create({
        name: promptName,
        content,
        generatedPrompt: optimizedPrompt && optimizedPrompt !== originalPrompt ? optimizedPrompt : '',
        description: `Generated via ${activeAction ?? 'dashboard'} action`,
        tags: [activeAction ?? 'dashboard'],
        isPublic: false,
        paiqScore: safePaiqScore,
        metadata: {
          originalPrompt,
          source: 'dashboard',
          actionType: activeAction ?? 'dashboard',
        },
      });

      const created = (response?.data ?? response) as Prompt | undefined;
      if (created?.entityId) {
        setPrompts((prev) => [created, ...prev]);
      }
      setSaveMessage(`Saved "${created?.promptName || promptName}" to library.`);

      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (error: unknown) {
      setActionError(extractApiErrorMessage(error, 'Failed to save prompt.'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {user?.email ? `Signed in as ${user.email}` : 'Your prompt optimization workspace'}
      </Typography>

      {/* Prompt input */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          What would you like to create?
        </Typography>
        <TextField
          multiline minRows={4} maxRows={10} fullWidth
          placeholder="Enter your prompt here… (minimum 20 characters to unlock actions)"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value.slice(0, MAX_CHARS))}
          inputProps={{ maxLength: MAX_CHARS }}
          sx={{ mb: 1 }}
        />
        <Typography variant="caption" color={promptText.length >= MAX_CHARS ? 'error' : 'text.secondary'} sx={{ display: 'block', textAlign: 'right' }}>
          {promptText.length} / {MAX_CHARS}
        </Typography>

        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
          {ACTION_BUTTONS.map((btn) => (
            <Button key={btn.type} variant={activeAction === btn.type ? 'contained' : 'outlined'}
              disabled={!buttonsEnabled || loading} onClick={() => handleAction(btn.type)}
              sx={{
                width: 130, height: 100, flexDirection: 'column', gap: 1,
                textTransform: 'none', fontSize: '0.9rem', fontWeight: 600, borderRadius: 2,
                borderColor: btn.color,
                color: activeAction === btn.type ? 'white' : btn.color,
                bgcolor: activeAction === btn.type ? btn.color : 'transparent',
                '&:hover': { bgcolor: activeAction === btn.type ? btn.color : `${btn.color}18`, borderColor: btn.color },
                '&.Mui-disabled': { borderColor: 'action.disabled', color: 'action.disabled' },
              }}
            >{btn.icon}{btn.label}</Button>
          ))}
        </Box>
        {!buttonsEnabled && promptText.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {MIN_CHARS - promptText.trim().length} more character{MIN_CHARS - promptText.trim().length !== 1 ? 's' : ''} needed
          </Typography>
        )}
      </Paper>

      {/* Results */}
      {(loading || actionError || resultPrompt || paiqResult) && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {activeAction ? (ACTION_BUTTONS.find(b => b.type === activeAction)?.label + ' Results') : 'Results'}
          </Typography>
          {loading && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}><CircularProgress size={24} /><Typography color="text.secondary">Generating and analyzing…</Typography></Box>}
          {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
          {saved && <Alert severity="success" sx={{ mb: 2 }}>Prompt saved to your library!</Alert>}

          {!loading && resultPrompt && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {activeAction === 'marketing-pack' ? 'Marketing Pack Prompt' : 'Generated Prompt'}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', mb: 1 }}>
                <MarkdownView>{resultPrompt}</MarkdownView>
              </Paper>
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopy(resultPrompt)} variant="outlined">
                  {copySuccess ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  variant="outlined"
                  disabled={saving || (!resultPrompt.trim() && !promptText.trim())}
                >
                  {saving ? 'Saving...' : 'Save to Library'}
                </Button>
              </Stack>
            </Box>
          )}

          {!loading && paiqResult && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>PAIQ Analysis</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', border: '4px solid', borderColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                  <Typography variant="h5" fontWeight="bold" lineHeight={1}>{paiqResult.paiqScore}</Typography>
                  <Typography variant="caption" color="text.secondary">/{paiqResult.maxScore}</Typography>
                </Box>
                <Chip label={paiqResult.paiqGrade} color={gradeColor(paiqResult.paiqGrade)} sx={{ fontWeight: 700 }} />
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowPaiqDetails(true)}
                  sx={{ textTransform: 'none', textDecoration: 'underline', ml: 'auto' }}
                >
                  View full analysis
                </Button>
              </Box>

              {paiqResult.dimensions && (
                <Box sx={{ mb: 2 }}>
                  {Object.entries(paiqResult.dimensions).map(([key, val]) => {
                    if (!val) return null;
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
                    return <DimensionBar key={key} label={label} score={val.score} />;
                  })}
                </Box>
              )}

              {paiqResult.strengths && paiqResult.strengths.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="success.main" gutterBottom>Strengths</Typography>
                  <Stack spacing={0.5}>{paiqResult.strengths.map((s, i) => <Typography key={i} variant="body2">• {s}</Typography>)}</Stack>
                </Box>
              )}

              {paiqResult.weaknesses && paiqResult.weaknesses.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="warning.main" gutterBottom>Areas to Improve</Typography>
                  <Stack spacing={0.5}>{paiqResult.weaknesses.map((w, i) => <Typography key={i} variant="body2">• {w}</Typography>)}</Stack>
                </Box>
              )}

              {paiqResult.rewrittenPrompt && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Suggested Rewrite</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', mb: 1 }}>
                    <MarkdownView dense>{paiqResult.rewrittenPrompt}</MarkdownView>
                  </Paper>
                  <Button size="small" startIcon={<ContentCopyIcon />} variant="outlined" onClick={() => handleCopy(paiqResult.rewrittenPrompt!)}>Copy Rewrite</Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      )}

      <Dialog open={showPaiqDetails} onClose={() => setShowPaiqDetails(false)} fullWidth maxWidth="md" scroll="paper">
        {/* Header — matches PAIQDialog gradient */}
        <Box sx={{ background: 'linear-gradient(135deg, #38bdf8 0%, #3b82f6 50%, #1e3a8a 100%)', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700} color="white">PAIQ — Full Analysis</Typography>
          <IconButton onClick={() => setShowPaiqDetails(false)} sx={{ color: 'white' }} size="small">✕</IconButton>
        </Box>

        <DialogContent dividers sx={{ minHeight: 400 }}>
          {paiqResult && (
            <Stack spacing={3}>
              {/* Score panel */}
              <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="body1" color="text.secondary" fontWeight={500}>PAIQ Score:</Typography>
                  <Typography variant="h3" fontWeight={700} color="primary.main">
                    {paiqResult.paiqScore}
                    <Typography component="span" variant="h5" color="primary.light">/{paiqResult.maxScore}</Typography>
                  </Typography>
                </Box>
                <Chip label={paiqResult.paiqGrade} color={gradeColor(paiqResult.paiqGrade)} sx={{ fontWeight: 600, fontSize: '0.9rem', px: 1 }} />
              </Paper>

              {/* Dimensions — collapsible, 2-column */}
              {paiqResult.dimensions && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box onClick={() => setShowDimensions(v => !v)} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', mb: showDimensions ? 2 : 0 }}>
                    <Typography variant="subtitle2">Score Breakdown by Dimension (1–5 each)</Typography>
                    <IconButton size="small">{showDimensions ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                  </Box>
                  <Collapse in={showDimensions}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4 }}>
                      {Object.entries(paiqResult.dimensions).map(([key, val]) => {
                        if (!val) return null;
                        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
                        const pct = (val.score / 5) * 100;
                        const barColor = pct >= 80 ? '#10b981' : pct >= 60 ? '#3b82f6' : pct >= 40 ? '#eab308' : '#ef4444';
                        return (
                          <Box key={key} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={500}>{label}</Typography>
                              <Typography variant="body2" fontWeight={700}>{val.score}/5</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={pct}
                              sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200', '& .MuiLinearProgress-bar': { backgroundColor: barColor, borderRadius: 4 } }}
                            />
                            {val.reasoning && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{val.reasoning}</Typography>}
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Paper>
              )}

              {/* Strengths & Weaknesses side-by-side */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {paiqResult.strengths && paiqResult.strengths.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}>
                    <Typography variant="subtitle2" color="success.dark" gutterBottom>✓ Strengths</Typography>
                    {paiqResult.strengths.map((s, i) => <Typography key={i} variant="body2" color="success.dark">• {s}</Typography>)}
                  </Paper>
                )}
                {paiqResult.weaknesses && paiqResult.weaknesses.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbeb', borderColor: '#fde68a' }}>
                    <Typography variant="subtitle2" color="warning.dark" gutterBottom>⚠ Weaknesses</Typography>
                    {paiqResult.weaknesses.map((w, i) => <Typography key={i} variant="body2" color="warning.dark">• {w}</Typography>)}
                  </Paper>
                )}
              </Box>

              {/* Improvements */}
              {paiqResult.improvements && paiqResult.improvements.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#eff6ff', borderColor: '#bfdbfe' }}>
                  <Typography variant="subtitle2" color="primary.dark" gutterBottom>💡 Improvements</Typography>
                  {paiqResult.improvements.map((imp, i) => <Typography key={i} variant="body2" color="primary.dark">• {imp}</Typography>)}
                </Paper>
              )}

              {/* Rewritten prompt */}
              {paiqResult.rewrittenPrompt && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f9ff', borderColor: '#7dd3fc' }}>
                  <Typography variant="subtitle2" color="info.dark" gutterBottom>
                    {activeAction === 'marketing-pack' ? 'Marketing Pack Prompt' : '✨ Rewritten Higher-PAIQ Prompt'}
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto', bgcolor: 'white', fontSize: '0.85rem' }}>
                    <MarkdownView dense>{paiqResult.rewrittenPrompt}</MarkdownView>
                  </Paper>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
          <Button startIcon={<ContentCopyIcon />} onClick={() => paiqResult?.rewrittenPrompt && handleCopy(paiqResult.rewrittenPrompt)}>
            {activeAction === 'marketing-pack' ? 'Copy Prompt' : 'Copy Rewrite'}
          </Button>
          <Button variant="contained" onClick={() => setShowPaiqDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(saveMessage)}
        autoHideDuration={4000}
        onClose={() => setSaveMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSaveMessage(null)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {saveMessage}
        </Alert>
      </Snackbar>


      {/* Library search bar */}
      {!libraryLoading && (
        <Box sx={{ mb: 3 }}>
          <TextField fullWidth size="small" placeholder="Search your library…" value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ maxWidth: 480 }}
          />
        </Box>
      )}

      {libraryLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
      {libraryError && <Alert severity="error" sx={{ mb: 3 }}>{libraryError}</Alert>}

      {/* Search results */}
      {!libraryLoading && filteredResults && (
        <Box sx={{ mb: 4 }}>
          {filteredResults.prompts.length || filteredResults.templates.length || filteredResults.packs.length ? (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>Results for "{searchQuery}"</Typography>
              {filteredResults.prompts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Prompts ({filteredResults.prompts.length})</Typography>
                  <Stack spacing={1}>{filteredResults.prompts.slice(0, 5).map(p => (
                    <Card key={p.entityId} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/prompts')}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LibraryBooksIcon color="primary" fontSize="small" /><Typography variant="body1">{p.promptName}</Typography>{p.paiqScore && <Chip label={`PAIQ: ${p.paiqScore}`} size="small" color="primary" />}</Box>
                      </CardContent>
                    </Card>
                  ))}</Stack>
                </Box>
              )}
              {filteredResults.templates.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Templates ({filteredResults.templates.length})</Typography>
                  <Stack spacing={1}>{filteredResults.templates.slice(0, 5).map(t => (
                    <Card key={t.entityId} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/templates')}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ArticleIcon color="secondary" fontSize="small" /><Typography variant="body1">{t.templateName}</Typography></Box></CardContent>
                    </Card>
                  ))}</Stack>
                </Box>
              )}
              {filteredResults.packs.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Packs ({filteredResults.packs.length})</Typography>
                  <Stack spacing={1}>{filteredResults.packs.slice(0, 5).map(p => (
                    <Card key={p.entityId} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/packs')}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><InventoryIcon color="success" fontSize="small" /><Typography variant="body1">{p.promptPackName}</Typography></Box></CardContent>
                    </Card>
                  ))}</Stack>
                </Box>
              )}
            </>
          ) : <Alert severity="info">No results found for "{searchQuery}"</Alert>}
        </Box>
      )}

      {/* Recent sections */}
      {!libraryLoading && !filteredResults && (
        <Box>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LibraryBooksIcon color="primary" /><Typography variant="h6">Recent Prompts</Typography></Box>
              <Button endIcon={<ArrowForwardIcon />} onClick={() => navigate('/prompts')}>Show More</Button>
            </Box>
            {recentPrompts.length > 0 ? (
              <Stack spacing={1}>{recentPrompts.map(p => (
                <Card key={p.entityId} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/prompts')}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LibraryBooksIcon color="primary" fontSize="small" /><Typography variant="body1">{p.promptName}</Typography>{p.paiqScore && <Chip label={`PAIQ: ${p.paiqScore}`} size="small" color="primary" />}</Box></CardContent>
                </Card>
              ))}</Stack>
            ) : <Typography variant="body2" color="text.secondary">No prompts yet. Create your first one above!</Typography>}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ArticleIcon color="secondary" /><Typography variant="h6">Recent Templates</Typography></Box>
              <Button endIcon={<ArrowForwardIcon />} onClick={() => navigate('/templates')}>Show More</Button>
            </Box>
            {recentTemplates.length > 0 ? (
              <Stack spacing={1}>{recentTemplates.map(t => (
                <Card key={t.entityId} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/templates')}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ArticleIcon color="secondary" fontSize="small" /><Typography variant="body1">{t.templateName}</Typography></Box></CardContent>
                </Card>
              ))}</Stack>
            ) : <Typography variant="body2" color="text.secondary">No templates yet.</Typography>}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><InventoryIcon color="success" /><Typography variant="h6">Recent Packs</Typography></Box>
              <Button endIcon={<ArrowForwardIcon />} onClick={() => navigate('/packs')}>Show More</Button>
            </Box>
            {recentPacks.length > 0 ? (
              <Stack spacing={1}>{recentPacks.map(p => (
                <Card key={p.entityId} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => navigate('/packs')}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><InventoryIcon color="success" fontSize="small" /><Typography variant="body1">{p.promptPackName}</Typography></Box></CardContent>
                </Card>
              ))}</Stack>
            ) : <Typography variant="body2" color="text.secondary">No packs yet.</Typography>}
          </Box>
        </Box>
      )}
    </Container>
  );
}